export type OrgEventActivityLike = {
  archived: boolean;
  created_at: string;
  updated_at: string;
  last_update: string | null;
};

export type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

type Listener = () => void;

const listeners = new Set<Listener>();
const snapshotByKey = new Map<string, string | null>();

function emit() {
  listeners.forEach((listener) => listener());
}

export function subscribeEventsSeenState(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function defaultStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

export function eventsSeenStorageKey(
  userGuid: string,
  groupGuid: string
): string {
  return `pratus:events:lastSeen:${userGuid}:${groupGuid}`;
}

export function peekLastSeenAt(
  key: string,
  storage: StorageLike | null = defaultStorage()
): string | null {
  if (!key || !storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function getLastSeenSnapshot(key: string): string | null {
  if (!key) return null;
  if (snapshotByKey.has(key)) return snapshotByKey.get(key) ?? null;
  const value = peekLastSeenAt(key);
  snapshotByKey.set(key, value);
  return value;
}

function persistLastSeen(
  key: string,
  value: string,
  storage: StorageLike | null
) {
  storage?.setItem(key, value);
  snapshotByKey.set(key, value);
  emit();
}

export function seedLastSeenAt(
  key: string,
  options?: {
    now?: () => string;
    storage?: StorageLike | null;
  }
): string {
  const storage =
    options?.storage === undefined ? defaultStorage() : options.storage;
  const existing = peekLastSeenAt(key, storage);
  if (existing) {
    snapshotByKey.set(key, existing);
    return existing;
  }
  const seeded = (options?.now ?? (() => new Date().toISOString()))();
  persistLastSeen(key, seeded, storage);
  return seeded;
}

export function markEventsSeen(
  key: string,
  options?: {
    now?: () => string;
    storage?: StorageLike | null;
  }
): string {
  const storage =
    options?.storage === undefined ? defaultStorage() : options.storage;
  const seenAt = (options?.now ?? (() => new Date().toISOString()))();
  persistLastSeen(key, seenAt, storage);
  return seenAt;
}

export function eventActivityMs(event: OrgEventActivityLike): number {
  return Math.max(
    Date.parse(event.updated_at) || 0,
    event.last_update ? Date.parse(event.last_update) || 0 : 0,
    Date.parse(event.created_at) || 0
  );
}

export function countUnseenOrgEvents(
  events: OrgEventActivityLike[],
  lastSeenAt: string | null
): number {
  if (!lastSeenAt) return 0;
  const seenMs = Date.parse(lastSeenAt);
  if (Number.isNaN(seenMs)) return 0;
  return events.filter((event) => {
    if (event.archived) return false;
    return eventActivityMs(event) > seenMs;
  }).length;
}
